import React from 'react';

import {
  BookOpenIcon,
  UserGroupIcon,
  ArrowRightIcon,
} from '@heroicons/react-v2/20/solid';
import Link from '../../components/Link';
import {
  PathIcon,
  StoryBookIcon,
} from '../../components/svg';
import {clampLines, resizeContentfulImage} from '../../util';

/**
 * Maps specific course tags to colored gradient overlays.
 * Only role-based tags get colored gradients; company tags and others
 * get a simple dark gradient. You can customize per-course by adding
 * entries here keyed by any tag the course has.
 */
const TAG_GRADIENTS = {
  pm: 'from-purple-600 to-indigo-700',
  'product-management': 'from-purple-600 to-indigo-700',
  swe: 'from-green-600 to-teal-600',
  'software-engineering': 'from-green-600 to-teal-600',
  'system-design': 'from-blue-600 to-blue-800',
  'data-analyst': 'from-blue-600 to-indigo-600',
  'data-engineer': 'from-blue-500 to-blue-700',
  'data-science': 'from-blue-600 to-blue-800',
  'ml-engineer': 'from-teal-600 to-green-600',
  'forward-deployed-engineer': 'from-orange-500 to-orange-700',
  em: 'from-yellow-600 to-yellow-700',
  tpm: 'from-yellow-600 to-yellow-700',
  behavioral: 'from-teal-600 to-green-600',
  security: 'from-red-600 to-pink-600',
  'product-designer': 'from-pink-600 to-red-500',
  'product-analyst': 'from-indigo to-purple-700',
  sql: 'from-blue-700 to-indigo',
  'solutions-architect': 'from-gray-700 to-gray-900',
  bizops: 'from-gray-700 to-gray-800',
};

// Default: a simple dark gradient for company-tagged or unmatched courses
const DEFAULT_GRADIENT = 'from-gray-800 to-gray-900';

const getGradient = (tags) => {
  if (!tags || !tags.length) return DEFAULT_GRADIENT;
  for (let i = 0; i < tags.length; i++) {
    if (TAG_GRADIENTS[tags[i]]) return TAG_GRADIENTS[tags[i]];
  }
  return DEFAULT_GRADIENT;
};

export const Placeholder = () => (
  <div className="flex flex-col rounded-2xl border border-gray-200 overflow-hidden animate-pulse bg-white">
    <div className="w-full bg-gray-100" style={{height: '180px'}} />
    <div className="w-full flex-shrink-0 p-4">
      <div className="h-5 bg-gray-100 rounded w-4/5 mb-2" />
      <div className="h-4 bg-gray-100 rounded w-full mb-1" />
      <div className="h-4 bg-gray-100 rounded w-3/4 mb-3" />
      <div className="h-10 bg-gray-100 rounded-xl w-full" />
    </div>
  </div>
);

const CourseCard = ({
  name,
  description,
  href,
  thumbnail,
  badge,
  numStudents,
  numLessons,
  numCourses,
  isPath,
  tags,
  onClick,
}) => {
  const gradient = getGradient(tags);

  return (
    <div className="relative flex flex-col rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all group bg-white focus-within:outline focus-within:outline-2 focus-within:outline-indigo">
      {/* Thumbnail — taller image area */}
      <div className="hidden md:flex relative justify-center items-center bg-gray-100 overflow-hidden flex-shrink-0" style={{height: '156px'}}>
        {thumbnail
          ? (
            <img
              src={resizeContentfulImage(thumbnail, 800, 800)}
              className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              width="400"
              height="200"
              alt={name}
            />
          ) : <StoryBookIcon className="w-10 h-10 text-gray-400" />}
        {/* Color gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t ${gradient} opacity-50`} />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-20" />
        {badge && (
          <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs font-bold px-2.5 py-1 rounded-full flex items-center" style={{gap: '4px'}}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
              <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
            </svg>
            {badge}
          </div>
        )}
        {/* Type badge on image */}
        <div className="absolute bottom-3 left-3">
          {isPath ? (
            <span className="inline-flex items-center text-gray-800 text-xs font-medium px-2.5 py-1 rounded-full" style={{gap: '4px', backgroundColor: 'rgba(255,255,255,0.9)'}}>
              <PathIcon className="h-3 w-3" />
              Learning Path
            </span>
          ) : (
            <span className="inline-flex items-center text-gray-800 text-xs font-medium px-2.5 py-1 rounded-full" style={{gap: '4px', backgroundColor: 'rgba(255,255,255,0.9)'}}>
              <BookOpenIcon className="w-3 h-3" />
              Course
            </span>
          )}
        </div>
      </div>

      {/* Content — compact padding */}
      <div className="flex flex-col justify-between w-full flex-1 p-4">
        <div className="flex flex-col items-start">
          {/* Mobile-only type badge */}
          <div className="md:hidden mb-1.5">
            {isPath ? (
              <div className="flex items-center bg-gray-800 text-white text-xs font-medium rounded-full px-2.5 py-1" style={{gap: '6px'}}>
                <PathIcon className="h-3 w-3" />
                Learning Path
              </div>
            ) : (
              <div className="flex items-center bg-gray-100 text-gray-700 text-xs font-medium rounded-full px-2.5 py-1" style={{gap: '6px'}}>
                <BookOpenIcon className="w-3 h-3" />
                Course
              </div>
            )}
          </div>
          <h3 className="font-semibold text-gray-900 text-base mb-1.5 leading-snug group-hover:text-indigo transition-colors">
            <Link
              href={href}
              onClick={onClick}
              className="after:absolute after:inset-0 after:content-[''] focus:outline-none"
            >
              {name}
            </Link>
          </h3>
          <div className="flex items-center text-xs text-gray-500 mb-2" style={{gap: '12px'}}>
            {numCourses > 0 && (
              <span className="flex items-center" style={{gap: '4px'}}>
                <BookOpenIcon className="w-3.5 h-3.5" />
                {`${numCourses} ${numCourses === 1 ? 'course' : 'courses'}`}
              </span>
            )}
            {numLessons > 0 && (
              <span className="flex items-center" style={{gap: '4px'}}>
                <BookOpenIcon className="w-3.5 h-3.5" />
                {`${numLessons} ${numLessons === 1 ? 'lesson' : 'lessons'}`}
              </span>
            )}
            {numStudents > 0 && (
              <span className="flex items-center" style={{gap: '4px'}}>
                <UserGroupIcon className="w-3.5 h-3.5" />
                {`${(Math.round(numStudents / 100) * 100).toLocaleString()} students`}
              </span>
            )}
          </div>
          <p
            className="text-sm text-gray-500 leading-relaxed mb-3"
            style={clampLines(2)}
          >
            {description}
          </p>
        </div>
        <span
          aria-hidden="true"
          className="w-full bg-gray-900 group-hover:bg-gray-800 text-white text-sm font-medium py-2.5 rounded-xl transition-all flex items-center justify-center"
          style={{gap: '8px'}}
        >
          Start learning
          <ArrowRightIcon className="w-4 h-4" />
        </span>
      </div>
    </div>
  );
};

export default CourseCard;
